import { getInfoValue, isVisible } from "@components/smart/FieldInfo";
import { isDefined } from "@utils/general";
import React from "react";

import SmartFormGroup, { IFormGroupDef } from "../../components/smart/smartFormGroup/SmartFormGroup";
import SmartFormTabs from "../../components/smart/smartFormGroup/SmartFormTabs";
import BindingContext, { IEntity } from "../../odata/BindingContext";
import { getGroupCollectionTitle } from "./Form.utils";
import { renderFormTable } from "./FormRender.utils";
import { IPureFormProps } from "./PureForm.types";

export type { IPureFormProps };

export class PureForm extends React.Component<IPureFormProps> {
    _handlers: Record<string, ((ref: unknown) => void)> = {};

    getRefHandler(groupId: string): ((ref: unknown) => void) {
        if (!this._handlers[groupId]) {
            this._handlers[groupId] = (ref: unknown) => {
                this.props.storage.addGroupRef(ref, groupId);
            };
        }
        return this._handlers[groupId];
    }



    renderStandardGroup = (group: IFormGroupDef, bindingContext: BindingContext, key: string, title?: string): React.ReactElement => {
        const { storage } = this.props;

        return (
            <SmartFormGroup
                key={key}
                ref={this.getRefHandler(group.id)}
                title={title}
                isSimple={this.props.isSimple}

                isInEdit={!storage.data.bindingContext?.isNew()}
                bindingContext={bindingContext}
                def={group}
                storage={storage}
                onExpand={this.props.onGroupExpand}
                onBlur={this.props.onBlur}
                onCancel={this.props.onCancel}
                onConfirm={this.props.onConfirm}
                onChange={this.props.onChange}
                onTemporalChange={this.props.onTemporalChange}
                onRemove={this.props.onRemove}
                onLineItemsChange={this.props.onLineItemChange}
                onLineItemsAction={this.props.onLineItemAction}
                onFieldStateChange={this.props.onFieldStateChange}
                onGroupAction={this.props.onGroupAction}
            />
        );
    };

    renderCollection = (group: IFormGroupDef, index: number) => {
        const { storage } = this.props;
        const { tooltip, render, ...info } = group;
        const collectionBc = storage.data.bindingContext.navigate(group.collection);
        if (isDefined(group.collectionKey)) {
            const collectionKey = getInfoValue(group, "collectionKey", { storage, bindingContext: collectionBc });
            return collectionKey ? this.renderStandardGroup(group, collectionBc.addKey(collectionKey), `${index}-${collectionKey}`) : null;
        }
        const collection = storage.getValue(collectionBc) ?? [];

        return collection.map((item: IEntity, i: number) => {
            const updatedGroupDef = { ...group };
            updatedGroupDef.title = getGroupCollectionTitle(storage, group, i, item);
            const bindingContext = collectionBc.addKey(item);

            if (!isVisible({
                info,
                bindingContext,
                storage,
                context: storage.context
            })) {
                return null;
            }

            return this.renderStandardGroup(updatedGroupDef, bindingContext, `${index}-${i}`);
        });
    };

    renderTabs = (group: IFormGroupDef, index: number) => {
        return (
            <SmartFormTabs ref={this.getRefHandler(group.id)}
                           key={group.id}
                           storage={this.props.storage}
                           group={group}
                           index={index}
                           renderGroup={this.renderGroup}
            />
        );
    };

    renderGroup = (group: IFormGroupDef, index: number, title?: string, isInTab?: boolean, topGroupId?: string) => {
        const { tooltip, render, ...info } = group;
        if (!this.props.storage.data.fieldsInfo) {
            return null;
        }
        // collection group has its own visibility check per item
        if (group.collection) {
            return this.renderCollection(group, index);
        }

        if (!isVisible({ info, storage: this.props.storage, context: this.props.storage.context })) {
            return null;
        }

        if (group.render) {
            return group.render(this.props.storage, group);
        }

        if (group.tabs) {
            return this.renderTabs(group, index);
        }

        if (group.table) {
            const key = this.props.storage.data.uuid ? `${group.table.id}-${this.props.storage.data.uuid}` : group.table.id;
            return renderFormTable(this.props.storage, group, group.table, this.props.storage.data.bindingContext, isInTab, key, topGroupId);
        }

        return this.renderStandardGroup(group, this.props.storage.data.bindingContext, index.toString(), title);
    };

    render() {
        return (
            <div style={this.props.style} ref={this.props.passRef}>
                {this.props.storage.getVariant().map((item: IFormGroupDef, i: number) => {
                    return this.renderGroup(item, i);
                })}
            </div>
        );
    }
}